import React, { useState } from 'react'
import GroupedTasks from './GroupedTasks'
import DataStateDisplay from './DataStateDisplay'
import { ErrorBoundary } from 'react-error-boundary'
import LifeGoals from './LifeGoals'
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { JourneyImportPanel } from '../JourneyImportPage';
import JourneyAiChatDialog from '../JourneyAiChatDialog';
import { loadGuestJourneyState } from '../guestJourneySession';
import { useData } from '../DataProviderContext';

const isLocalRuntime = () => {
    const hostname = window.location.hostname;
    return process.env.NODE_ENV !== 'production'
        || hostname === 'localhost'
        || hostname === '127.0.0.1'
        || hostname === '';
};

export const isAiJourneyChatEnabled = (
    environment = process.env.REACT_APP_ENVIRONMENT,
    nodeEnvironment = process.env.NODE_ENV,
) => (
    environment === 'staging'
    || environment === 'production'
    || nodeEnvironment !== 'production'
);

function MultiView(props) {
    const { dataState } = useData();
    const [isAiChatOpen, setIsAiChatOpen] = useState(() => {
        const guest = loadGuestJourneyState();
        return Boolean(guest?.phase === 'chat' && guest.messages.length > 0);
    });
    return (
        <Box
            data-testid="journey-board"
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                bgcolor: 'grey.50',
                color: 'text.primary',
                minHeight: 'calc(100vh - 64px)',
                textAlign: 'left',
            }}
        >
            <Container
                maxWidth={false}
                sx={{ width: '100%', maxWidth: 1120, px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4, md: 6 } }}
            >
                <Stack spacing={{ xs: 3, md: 4 }}>
                    <Paper
                        component="section"
                        aria-labelledby="journey-board-title"
                        variant="outlined"
                        data-testid="journey-board-hero"
                        sx={{
                            p: { xs: 3, sm: 4, md: 5 },
                            borderRadius: 2,
                            borderTop: 6,
                            borderTopColor: 'primary.main',
                            boxShadow: 1,
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={{ xs: 3, md: 5 }}
                            alignItems={{ xs: 'stretch', md: 'flex-start' }}
                            justifyContent="space-between"
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                                    Journey board
                                </Typography>
                                <Typography
                                    id="journey-board-title"
                                    variant="h3"
                                    component="h1"
                                    sx={{
                                        mt: 0.5,
                                        maxWidth: '18ch',
                                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                        fontWeight: 850,
                                        lineHeight: 1.08,
                                        letterSpacing: '-0.025em',
                                        overflowWrap: 'anywhere',
                                    }}
                                >
                                    Your Journey Board
                                </Typography>
                                <Typography
                                    variant="h6"
                                    component="p"
                                    color="text.secondary"
                                    sx={{ mt: 2, maxWidth: '68ch', lineHeight: 1.65 }}
                                >
                                    Break a meaningful goal into clear steps, keep your next actions visible, and update the plan as you learn.
                                </Typography>
                            </Box>
                            {isAiJourneyChatEnabled() ? (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AutoAwesomeOutlinedIcon />}
                                onClick={() => setIsAiChatOpen(true)}
                                sx={{
                                    minHeight: 48,
                                    width: { xs: '100%', md: 'auto' },
                                    minWidth: { md: 224 },
                                    flexShrink: 0,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Create a journey with AI
                            </Button>
                            ) : null}
                        </Stack>
                    </Paper>

                    <ErrorBoundary fallback={<div>Something went wrong</div>}>
                        <LifeGoals></LifeGoals>
                        {!dataState.guestJourneyStatus ? <JourneyImportPanel /> : null}
                        <GroupedTasks onOpenAiChat={() => setIsAiChatOpen(true)}>
                        </GroupedTasks>
                    </ErrorBoundary>
                    {isLocalRuntime() ? (
                        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                            <DataStateDisplay />
                        </Box>
                    ) : null}
                </Stack>
            </Container>
            {isAiJourneyChatEnabled() ? (
                <JourneyAiChatDialog
                    open={isAiChatOpen}
                    onClose={() => setIsAiChatOpen(false)}
                    onGenerated={() => setIsAiChatOpen(false)}
                />
            ) : null}
        </Box>
    )
}

//<JourneyView source={props}/>
//<JourneyView/>


export default MultiView
